import React, { type ReactNode } from "react"
import { useLocation } from "react-router-dom"

import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"
import { cn } from "@be-miniapp/ui/lib/utils"

import { Button } from "./button"
import { ArrowLeftIcon } from "./icons/arrow-left"
import { CloseIcon } from "./icons/close"
import { Typography } from "./typography"

export interface ActionBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "prefix"> {
  back?: boolean
  prefix?: ReactNode
  suffix?: ReactNode
  border?: boolean
  bold?: boolean
  ref?: React.Ref<HTMLDivElement>
  onBack?: () => void
  xIcon?: boolean
}

const BackButton = ({ onBack, xIcon }: any) => {
  const navigate = useNavigate()
  const location = useLocation()
  return (
    <Button
      type="button"
      variant="ghost"
      size="auto"
      className="h-full w-12 shrink-0 justify-center"
      onClick={
        onBack
          ? onBack
          : () => {
              const doesAnyHistoryEntryExist = location.key !== "default"

              if (!doesAnyHistoryEntryExist) {
                navigate("/ride/home", {
                  replace: true,
                  direction: "backward",
                })
                return
              }
              navigate(-1)
            }
      }
    >
      {xIcon ? <CloseIcon /> : <ArrowLeftIcon name="arrow-left" />}
    </Button>
  )
}

const ActionBar = React.forwardRef((props: ActionBarProps, ref) => {
  return (
    <div
      ref={ref as any}
      className={cn("bg-white pt-safe-top", props.className)}
    >
      <div
        className={cn(
          "flex h-11 shrink-0 items-center pr-4",
          props.border && "border-gray-100 border-b",
          !props.back && "gap-3 pl-4"
        )}
      >
        {props.back && <BackButton onBack={props.onBack} xIcon={props.xIcon} />}
        {!props.back && props.prefix}
        <Typography.Title variant="bold" size="medium" className="truncate">
          {props.children}
        </Typography.Title>
        <div className="ml-auto flex shrink-0">
          <div className={cn("mr-2 ml-auto text-blue-800")}>{props.suffix}</div>
        </div>
      </div>
    </div>
  )
})

ActionBar.displayName = "ActionBar"

export { ActionBar }
